import React, { useState } from 'react';
import { Input, Button, Space, message, Form as AntForm, Tooltip } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';

import useAuthStore from '../../store/useAuthStore';
import usePermissions from '../../hooks/usePermissions';
import { MODULE_IDS } from '../../utils/constants';
import { formatDate } from '../../utils/formatters';
import { getProducts, createProduct, updateProduct, deleteProduct } from '../../api/readyProducts.api';

import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import FormDrawer from '../../components/common/FormDrawer';
import ConfirmDelete from '../../components/common/ConfirmDelete';

const ReadyProductPage = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchText, setSearchText] = useState('');
  
  
  const [tableParams, setTableParams] = useState({ current: 1, pageSize: 50 });

  const { user } = useAuthStore();
  const plantId = user?.plantID;
  const queryClient = useQueryClient();

  const { canAdd, canEdit, canDelete } = usePermissions(MODULE_IDS.READY_PRODUCT);

  const { control, handleSubmit, reset, formState: { errors, isDirty } } = useForm({
    defaultValues: {
      productName: '',
      packSize: ''
    }
  });


  const { data, isLoading: isFetching } = useQuery({
    queryKey: ['readyProducts', plantId, searchText, tableParams.current, tableParams.pageSize],
    queryFn: () => getProducts(plantId, { 
      search: searchText, 
      page: tableParams.current, 
      pageSize: tableParams.pageSize 
    }),
    enabled: !!plantId,
  });

  
  const products = data?.items || [];
  const totalCount = data?.total || 0;

  
  const createMutation = useMutation({
    mutationFn: (data) => createProduct(plantId, data),
    onSuccess: () => {
      message.success('Ready Product created successfully!');
      queryClient.invalidateQueries({ queryKey: ['readyProducts'] });
      closeDrawer();
    },
    onError: (err) => {
      message.error(err.message.includes('exists') ? 'Product already exists in this plant.' : err.message);
    }
  });

  const updateMutation = useMutation({
    mutationFn: (data) => updateProduct(plantId, editingProduct.rpid, data),
    onSuccess: () => {
      message.success('Ready Product updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['readyProducts'] });
      closeDrawer();
    },
    onError: (err) => {
      message.error(err.message.includes('exists') ? 'Product already exists in this plant.' : err.message);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (rpid) => deleteProduct(plantId, rpid),
    onSuccess: () => {
      message.success('Ready Product deleted successfully!');
      queryClient.invalidateQueries({ queryKey: ['readyProducts'] });
    },
    onError: (err) => {
      message.error(err.message.includes('Cannot delete') 
        ? 'Cannot delete: product has related records (production or mapping).' 
        : err.message);
    }
  });


  const openDrawer = (record = null) => {
    setEditingProduct(record);
    if (record) {
      reset({
        productName: record.productName,
        packSize: record.packSize
      });
    } else {
      reset({ productName: '', packSize: '' });
    }
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingProduct(null);
    reset();
  };

  const onSubmit = (formData) => {
    if (editingProduct) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleTableChange = (pagination) => {
    setTableParams({
      current: pagination.current,
      pageSize: pagination.pageSize,
    });
  };

  const handleSearch = (val) => {
    setSearchText(val);
    setTableParams(prev => ({ ...prev, current: 1 })); 
  };


  const columns = [
    { 
      title: '#', 
      dataIndex: 'index', 
      key: 'index', 
      render: (text, record, index) => (tableParams.current - 1) * tableParams.pageSize + index + 1, 
      width: 60 
    },
    { title: 'Product Name', dataIndex: 'productName', key: 'productName' },
    { title: 'Pack/Size', dataIndex: 'packSize', key: 'packSize' },
    { title: 'Created On', dataIndex: 'createdOn', key: 'createdOn', render: (date) => formatDate(date) },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space size="middle">
          {canEdit && (
            <Tooltip title="Edit">
              <Button type="text" icon={<EditOutlined style={{ color: '#1890ff' }} />} onClick={() => openDrawer(record)} />
            </Tooltip>
          )}
          {canDelete && (
            <ConfirmDelete 
              itemName={record.productName} 
              onConfirm={() => deleteMutation.mutate(record.rpid)}
              loading={deleteMutation.isPending && deleteMutation.variables === record.rpid}
            />
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      <PageHeader 
        title="Ready Product Master" 
        subtitle="Manage the finished products manufactured in the plant"
        onAdd={() => openDrawer(null)} 
        canAdd={canAdd} 
        addLabel="Add Product"
      />

      <div style={{ marginBottom: 16, maxWidth: 400 }}>
        <Input.Search 
          placeholder="Search by product name..." 
          allowClear 
          onSearch={handleSearch}
        />
      </div>

      <DataTable 
        columns={columns} 
        data={products} 
        loading={isFetching} 
        rowKey="rpid"
        pagination={{
          current: tableParams.current,
          pageSize: tableParams.pageSize,
          total: totalCount
        }}
        onChange={handleTableChange}
      />

      <FormDrawer
        title={editingProduct ? "Edit Ready Product" : "Add Ready Product"}
        open={drawerOpen}
        onClose={closeDrawer}
        onSave={handleSubmit(onSubmit)}
        loading={createMutation.isPending || updateMutation.isPending}
        isDirty={isDirty}
        width={400}
      >
        <AntForm layout="vertical">
          <AntForm.Item 
            label="Product Name" 
            validateStatus={errors.productName ? 'error' : ''} 
            help={errors.productName?.message}
            required
          >
            <Controller
              name="productName"
              control={control}
              rules={{ 
                required: 'Product name is required',
                maxLength: { value: 100, message: 'Max 100 characters allowed' }
              }}
              render={({ field }) => <Input {...field} placeholder="e.g. RO Water Bottle 1L" />}
            />
          </AntForm.Item>

          <AntForm.Item 
            label="Pack/Size" 
            validateStatus={errors.packSize ? 'error' : ''} 
            help={errors.packSize?.message}
          >
            <Controller
              name="packSize"
              control={control}
              rules={{ maxLength: { value: 50, message: 'Max 50 characters allowed' } }}
              render={({ field }) => (
                <Input 
                  {...field} 
                  placeholder="e.g. PC, DOZEN, CASE OF 24" 
                />
              )}
            />
          </AntForm.Item>
        </AntForm>
      </FormDrawer>
    </div>
  );
};

export default ReadyProductPage;